import type { STPreset, WorldBookEntry } from '@/IsekaiRPG/modules/llm/llmTypes';

export interface STVariableMacroState {
  local: Record<string, string>;
  global: Record<string, string>;
}

const PRIMARY_PROMPT_ORDER_CHARACTER_ID = 100001;

type VariableScope = 'local' | 'global';
type SupportedBlockMacro = 'setvar' | 'addvar' | 'setglobalvar' | 'addglobalvar';

type ParsedBlockMacro = {
  macro: SupportedBlockMacro;
  name: string;
};

type ParsedInlineMacro =
  | {
      macro: 'setvar' | 'addvar' | 'setglobalvar' | 'addglobalvar';
      name: string;
      value: string;
    }
  | {
      macro: 'getvar' | 'getglobalvar' | 'incvar' | 'decvar' | 'incglobalvar' | 'decglobalvar';
      name: string;
    };

type ResolveSegmentResult = {
  output: string;
  index: number;
  closed: boolean;
};

export class STVariableMacroResolver {
  static createState(): STVariableMacroState {
    return {
      local: {},
      global: {},
    };
  }

  static applyToPreset(preset: STPreset, state: STVariableMacroState): STPreset {
    if (!preset.enabled) {
      return preset;
    }

    const activePromptIdentifiers = this.getActivePromptIdentifiers(preset);

    return {
      ...preset,
      prompts: preset.prompts.map(prompt => {
        if (!activePromptIdentifiers.has(prompt.identifier)) {
          return prompt;
        }

        return {
          ...prompt,
          content: this.resolveText(prompt.content, state),
        };
      }),
    };
  }

  static applyToWorldBookEntries(entries: WorldBookEntry[], state: STVariableMacroState): WorldBookEntry[] {
    return entries.map(entry => ({
      ...entry,
      content: this.resolveText(entry.content, state),
    }));
  }

  private static getActivePromptIdentifiers(preset: STPreset): Set<string> {
    const primaryOrderBlock =
      preset.prompt_order.find(block => block.character_id === PRIMARY_PROMPT_ORDER_CHARACTER_ID) ??
      preset.prompt_order[0];

    const enabledOrderIdentifiers = new Set(
      (primaryOrderBlock?.order ?? []).filter(entry => entry.enabled).map(entry => entry.identifier),
    );

    return new Set(
      preset.prompts.filter(prompt => enabledOrderIdentifiers.has(prompt.identifier)).map(prompt => prompt.identifier),
    );
  }

  private static resolveText(text: string, state: STVariableMacroState): string {
    if (!text.includes('{{')) return text;
    return this.resolveSegment(text, 0, state).output;
  }

  private static resolveSegment(
    text: string,
    startIndex: number,
    state: STVariableMacroState,
    expectedClosingMacro?: SupportedBlockMacro,
  ): ResolveSegmentResult {
    let output = '';
    let index = startIndex;

    while (index < text.length) {
      const tokenStart = text.indexOf('{{', index);
      if (tokenStart === -1) {
        output += text.slice(index);
        return { output, index: text.length, closed: false };
      }

      output += text.slice(index, tokenStart);

      const tokenEnd = text.indexOf('}}', tokenStart + 2);
      if (tokenEnd === -1) {
        output += text.slice(tokenStart);
        return { output, index: text.length, closed: false };
      }

      const rawToken = text.slice(tokenStart, tokenEnd + 2);
      const tokenBody = text.slice(tokenStart + 2, tokenEnd).trim();

      if (expectedClosingMacro && tokenBody === `/${expectedClosingMacro}`) {
        return {
          output,
          index: tokenEnd + 2,
          closed: true,
        };
      }

      const blockMacro = this.parseBlockMacro(tokenBody);
      if (blockMacro) {
        const inner = this.resolveSegment(text, tokenEnd + 2, state, blockMacro.macro);
        if (!inner.closed) {
          output += text.slice(tokenStart);
          return { output, index: text.length, closed: false };
        }

        this.executeWriteMacro(blockMacro.macro, blockMacro.name, inner.output, state);
        index = inner.index;
        continue;
      }

      const inlineMacro = this.parseInlineMacro(tokenBody);
      if (inlineMacro) {
        output += this.executeInlineMacro(inlineMacro, state);
        index = tokenEnd + 2;
        continue;
      }

      output += rawToken;
      index = tokenEnd + 2;
    }

    return { output, index: text.length, closed: false };
  }

  private static parseBlockMacro(tokenBody: string): ParsedBlockMacro | null {
    const match = /^(setvar|addvar|setglobalvar|addglobalvar)::([\s\S]+)$/.exec(tokenBody);
    if (!match) return null;

    const name = match[2].trim();
    if (!name || name.includes('::')) return null;

    return {
      macro: match[1] as SupportedBlockMacro,
      name,
    };
  }

  private static parseInlineMacro(tokenBody: string): ParsedInlineMacro | null {
    const writeMatch = /^(setvar|addvar|setglobalvar|addglobalvar)::([\s\S]+?)::([\s\S]*)$/.exec(tokenBody);
    if (writeMatch) {
      const name = writeMatch[2].trim();
      if (!name) return null;
      return {
        macro: writeMatch[1] as ParsedInlineMacro['macro'],
        name,
        value: writeMatch[3],
      } as ParsedInlineMacro;
    }

    const readOrMathMatch = /^(getvar|getglobalvar|incvar|decvar|incglobalvar|decglobalvar)::([\s\S]+)$/.exec(
      tokenBody,
    );
    if (!readOrMathMatch) return null;

    const name = readOrMathMatch[2].trim();
    if (!name) return null;

    return {
      macro: readOrMathMatch[1] as ParsedInlineMacro['macro'],
      name,
    } as ParsedInlineMacro;
  }

  private static executeInlineMacro(macro: ParsedInlineMacro, state: STVariableMacroState): string {
    switch (macro.macro) {
      case 'setvar':
      case 'addvar':
      case 'setglobalvar':
      case 'addglobalvar':
        this.executeWriteMacro(macro.macro, macro.name, macro.value, state);
        return '';
      case 'getvar':
        return this.getValue('local', macro.name, state);
      case 'getglobalvar':
        return this.getValue('global', macro.name, state);
      case 'incvar':
        return this.incrementValue('local', macro.name, 1, state);
      case 'decvar':
        return this.incrementValue('local', macro.name, -1, state);
      case 'incglobalvar':
        return this.incrementValue('global', macro.name, 1, state);
      case 'decglobalvar':
        return this.incrementValue('global', macro.name, -1, state);
      default:
        return '';
    }
  }

  private static executeWriteMacro(
    macro: SupportedBlockMacro | 'setvar' | 'addvar' | 'setglobalvar' | 'addglobalvar',
    name: string,
    value: string,
    state: STVariableMacroState,
  ): void {
    const scope = macro.includes('global') ? 'global' : 'local';

    if (macro.startsWith('set')) {
      this.setValue(scope, name, value, state);
      return;
    }

    this.addValue(scope, name, value, state);
  }

  private static getValue(scope: VariableScope, name: string, state: STVariableMacroState): string {
    return state[scope][name] ?? '';
  }

  private static setValue(scope: VariableScope, name: string, value: string, state: STVariableMacroState): void {
    state[scope][name] = value;
  }

  private static addValue(scope: VariableScope, name: string, value: string, state: STVariableMacroState): void {
    const currentValue = this.getValue(scope, name, state);

    if (this.isNumeric(currentValue) && this.isNumeric(value)) {
      state[scope][name] = String(Number(currentValue) + Number(value));
      return;
    }

    state[scope][name] = `${currentValue}${value}`;
  }

  private static incrementValue(
    scope: VariableScope,
    name: string,
    delta: number,
    state: STVariableMacroState,
  ): string {
    const currentValue = this.getValue(scope, name, state);
    const baseValue = this.isNumeric(currentValue) ? Number(currentValue) : 0;
    const nextValue = String(baseValue + delta);
    state[scope][name] = nextValue;
    return nextValue;
  }

  private static isNumeric(value: string): boolean {
    if (value.trim() === '') return false;
    return Number.isFinite(Number(value));
  }
}
